"use client";

import axios from "axios";
import Cookies from "js-cookie";
import { useLocale } from "@lib/i18n/navigation-pages-router";
import {
  useCurrentUser,
  useSetUserHeaderLastVersion,
  useSetClosetHeaderLastVersion,
} from "@hooks/use-current-user";
import Bugsnag from "@bugsnag/js";
import isServer from "@utils/is-server";

const axiosInstance = axios.create({
  baseURL:
    isServer() && process.env.NEXT_PUBLIC_SERVER_SIDE_API_URL
      ? process.env.NEXT_PUBLIC_SERVER_SIDE_API_URL
      : process.env.NEXT_PUBLIC_API_URL,
  headers: {
    "X-App-Origin": "NextJS",
    "X-App-Version": process.env.HEROKU_RELEASE_VERSION,
  },
});

const AxiosInterceptor = ({ children }) => {
  const user = useCurrentUser();
  const setUserHeaderLastVersion = useSetUserHeaderLastVersion();
  const setClosetHeaderLastVersion = useSetClosetHeaderLastVersion();
  const locale = useLocale();

  function checkUserLastVersion(userHeaderLastVersion) {
    if (!userHeaderLastVersion) return;
    if (user?.last_version === userHeaderLastVersion) return;

    setUserHeaderLastVersion(userHeaderLastVersion);
  }

  function checkClosetLastVersion(closetHeaderLastVersion) {
    if (!closetHeaderLastVersion) return;
    if (user?.last_closet_version === closetHeaderLastVersion) return;

    setClosetHeaderLastVersion(closetHeaderLastVersion);
  }

  const reqInterceptor = (config) => {
    if (!isServer()) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Language = locale;

      const jwt = Cookies.get("jwt-token");
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${jwt}`;
    }

    return config;
  };

  const resInterceptor = (response) => {
    if (!isServer()) {
      const jwt = response.headers["jwt-token"];
      const jwtAttributes = {
        expires: 14,
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        sameSite: "None",
        secure: true,
      };

      if (jwt) {
        Cookies.set("jwt-token", jwt, jwtAttributes);
      }

      checkClosetLastVersion(response.headers["x-user-last-closet-version"]);
      checkUserLastVersion(response.headers["x-user-last-version"]);
    }

    return response;
  };

  const errInterceptor = (error) => {
    Bugsnag.leaveBreadcrumb(
      "Axios Request Failed",
      {
        error: error.message,
        stack: error.stack,
        method: error.config?.method,
        url: error.config?.url,
      },
      "error",
    );

    return Promise.reject(error);
  };

  axiosInstance.interceptors.request.clear();
  axiosInstance.interceptors.response.clear();

  axiosInstance.interceptors.request.use(reqInterceptor);
  axiosInstance.interceptors.response.use(resInterceptor, errInterceptor);

  return children;
};

export default axiosInstance;
export { AxiosInterceptor };
